/* eslint-disable max-lines */
import {
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  StreamId,
  VenueArchiveLayout,
  VenueConnectionData,
  VenueSessionLayout,
} from "../../app/types";

interface VolumeSample {
  volume: number;
  date: number;
}

interface VenueSessionReduxState {
  sessionId: number;
  volume: Partial<
    Record<
      StreamId,
      { samples: VolumeSample[]; average: number; streamId: StreamId }
    >
  >;
  presenters: Partial<Record<StreamId, VenueConnectionData>>;
  layout: VenueSessionLayout;
  composedLayout: VenueArchiveLayout;
  subscribers: VenueConnectionData[];
  myStreams: {
    screen: StreamId | null;
    camera: StreamId | null;
  };
  settings: {
    showPublisherUI: boolean;
    isScreenSharing: boolean;
    publishAudio: boolean;
    publishVideo: boolean;
    isMuted: boolean;
    isFullScreen: boolean;
  };
}

export type SessionSetting = keyof VenueSessionReduxState["settings"];

export const sessionEntities = createEntityAdapter<VenueSessionReduxState>({
  selectId: (session) => session.sessionId,
});

const selectors = sessionEntities.getSelectors();

export function initialSessionState(sessionId: number): VenueSessionReduxState {
  return {
    sessionId,
    presenters: {},
    subscribers: [],
    layout: {
      screenShare: null,
      panel: [],
    },
    composedLayout: {},
    volume: {},
    myStreams: {
      screen: null,
      camera: null,
    },
    settings: {
      showPublisherUI: false,
      isScreenSharing: false,
      publishAudio: true,
      publishVideo: true,
      isMuted: false,
      isFullScreen: false,
    },
  };
}

const addStreamToLayout = (
  layout: VenueSessionLayout,
  streamId: string,
  videoType: OT.Stream["videoType"],
) => {
  if (videoType === "screen") {
    return {
      ...layout,
      screenShare: streamId,
    };
  }
  return {
    ...layout,
    panel: [...layout.panel, streamId],
  };
};

const removeStreamFromLayout = (
  layout: VenueSessionLayout,
  streamId: string,
  videoType: OT.Stream["videoType"],
) => {
  if (videoType === "screen") {
    return {
      ...layout,
      screenShare: null,
    };
  }

  return {
    ...layout,
    panel: layout.panel.filter((id) => id !== streamId),
  };
};

const sessionSlice = createSlice({
  name: "sessions",
  initialState: sessionEntities.getInitialState,
  reducers: {
    removeMyStream: {
      prepare: (
        sessionId: number,
        streamId: StreamId,
        type: OT.Stream["videoType"],
      ) => ({
        payload: { sessionId, streamId, type },
      }),
      reducer: (
        state,
        action: PayloadAction<{
          sessionId: number;
          streamId: StreamId;
          type: OT.Stream["videoType"];
        }>,
      ) => {
        const { sessionId, streamId, type } = action.payload;
        const oldState =
          state.entities[sessionId] ?? initialSessionState(sessionId);
        const myStreams = {
          ...oldState.myStreams,
        };
        if (type === "screen" && myStreams.screen === streamId) {
          myStreams.screen = null;
        } else if (type === "camera" && myStreams.camera === streamId) {
          myStreams.camera = null;
        }
        return sessionEntities.upsertOne(state, {
          ...oldState,
          myStreams,
        });
      },
    },
    addMyStream: {
      prepare: (
        sessionId: number,
        streamId: StreamId,
        type: OT.Stream["videoType"],
      ) => ({
        payload: { sessionId, streamId, type },
      }),
      reducer: (
        state,
        action: PayloadAction<{
          sessionId: number;
          streamId: StreamId;
          type: OT.Stream["videoType"];
        }>,
      ) => {
        const { sessionId, streamId, type } = action.payload;
        const oldState =
          state.entities[sessionId] ?? initialSessionState(sessionId);
        const myStreams = {
          ...oldState.myStreams,
        };
        if (type === "screen") {
          myStreams.screen = streamId;
        } else {
          myStreams.camera = streamId;
        }
        return sessionEntities.upsertOne(state, {
          ...oldState,
          myStreams,
        });
      },
    },
    addPresenter: {
      prepare: (
        sessionId: number,
        data: VenueConnectionData,
        streamId: string,
        videoType: OT.Stream["videoType"],
      ) => ({
        payload: { data, sessionId, streamId, videoType },
      }),
      reducer: (
        state,
        action: PayloadAction<{
          data: VenueConnectionData;
          sessionId: number;
          streamId: string;
          videoType: OT.Stream["videoType"];
        }>,
      ) => {
        const { data, sessionId, streamId, videoType } = action.payload;
        const oldState =
          state.entities[sessionId] ?? initialSessionState(sessionId);
        return sessionEntities.upsertOne(state, {
          ...oldState,
          presenters: {
            ...oldState.presenters,
            [streamId]: data,
          },
          layout: addStreamToLayout(oldState.layout, streamId, videoType),
        });
      },
    },
    removePresenter: {
      prepare: (
        sessionId: number,
        streamId: string,
        videoType: OT.Stream["videoType"],
      ) => ({
        payload: { sessionId, streamId, videoType },
      }),
      reducer: (
        state,
        action: PayloadAction<{
          sessionId: number;
          streamId: string;
          videoType: OT.Stream["videoType"];
        }>,
      ) => {
        const { sessionId: id, streamId, videoType } = action.payload;
        const oldState = state.entities[id] ?? initialSessionState(id);
        const { streamId: _, ...oldPresenters } = oldState.presenters;
        return sessionEntities.updateOne(state, {
          id,
          changes: {
            presenters: oldPresenters,
            layout: removeStreamFromLayout(
              oldState.layout,
              streamId,
              videoType,
            ),
          },
        });
      },
    },
    addSubscriber: {
      prepare: (sessionId: number, data: VenueConnectionData) => ({
        payload: { sessionId, data },
      }),
      reducer: (
        state,
        action: PayloadAction<{ sessionId: number; data: VenueConnectionData }>,
      ) => {
        const { data, sessionId } = action.payload;
        const oldState =
          state.entities[sessionId] ?? initialSessionState(sessionId);
        return sessionEntities.upsertOne(state, {
          ...oldState,
          subscribers: [
            ...oldState.subscribers.filter((sub) => sub.id !== data.id),
            data,
          ],
        });
      },
    },
    removeSubscriber: {
      prepare: (sessionId: number, data: VenueConnectionData) => ({
        payload: { sessionId, data },
      }),
      reducer: (
        state,
        action: PayloadAction<{ sessionId: number; data: VenueConnectionData }>,
      ) => {
        const { data, sessionId: id } = action.payload;
        const oldState = state.entities[id] ?? initialSessionState(id);
        return sessionEntities.updateOne(state, {
          id,
          changes: {
            subscribers: oldState.subscribers.filter(
              (sub) => sub.id !== data.id,
            ),
          },
        });
      },
    },
    updateVolume: {
      prepare: (sessionId: number, streamId: StreamId, volume: number) => ({
        payload: { sessionId, streamId, volume },
      }),
      reducer: (
        state,
        action: PayloadAction<{
          sessionId: number;
          streamId: StreamId;
          volume: number;
        }>,
      ) => {
        const { streamId, volume, sessionId } = action.payload;
        const oldState =
          selectors.selectById(state, sessionId) ??
          initialSessionState(sessionId);
        const oldSamples = oldState.volume[streamId]?.samples ?? [];
        const date = Date.now();
        const samples = [
          { volume, date },
          ...oldSamples.filter((sample) => {
            const diff = date - sample.date;
            return diff < 3000;
          }),
        ];
        const average =
          samples.reduce((prev, sample) => prev + sample.volume, 0) /
          samples.length;
        return sessionEntities.upsertOne(state, {
          ...oldState,
          volume: {
            ...oldState.volume,
            [streamId]: {
              samples,
              average,
              streamId,
            },
          },
        });
      },
    },
    setSessionSetting: {
      prepare: (
        sessionId: number | undefined,
        setting: SessionSetting,
        value: boolean,
      ) => ({
        payload: { sessionId, setting, value },
      }),
      reducer: (
        state,
        action: PayloadAction<{
          sessionId: number | undefined;
          setting: SessionSetting;
          value: boolean;
        }>,
      ) => {
        const { sessionId, setting, value } = action.payload;
        if (sessionId === undefined) {
          return state;
        }
        const oldState =
          selectors.selectById(state, sessionId) ??
          initialSessionState(sessionId);
        return sessionEntities.upsertOne(state, {
          ...oldState,
          settings: {
            ...oldState.settings,
            [setting]: value,
          },
        });
      },
    },
    setComposedLayout: {
      prepare: (sessionId: number, composedLayout: VenueArchiveLayout) => ({
        payload: { sessionId, composedLayout },
      }),
      reducer: (
        state,
        action: PayloadAction<{
          composedLayout: VenueArchiveLayout;
          sessionId: number;
        }>,
      ) => {
        const { composedLayout, sessionId } = action.payload;
        const sessionState =
          selectors.selectById(state, sessionId) ??
          initialSessionState(sessionId);
        return sessionEntities.upsertOne(state, {
          ...sessionState,
          composedLayout,
        });
      },
    },
  },
});

export const {
  updateVolume,
  addPresenter,
  removePresenter,
  addSubscriber,
  removeSubscriber,
  addMyStream,
  removeMyStream,
  setSessionSetting,
  setComposedLayout,
} = sessionSlice.actions;

export default sessionSlice;
